import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import useGatewayContagem from '../../Context/Hooks/useGatewayContagem';

const GatewayContagemFilters = ({
  showDateRange = true,
  showClient = true,
  showStore = false,
  showDevice = false,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const { t } = useTranslation();
  const { clients, stores, devices, selectedClient, selectedStore, selectClient, selectStore } = useGatewayContagem();

  const sortedDevices = useMemo(() => {
    if (!Array.isArray(devices)) {
      return [];
    }
    return [...devices].sort((a, b) => {
      const aCurrent = a.isCurrent !== false;
      const bCurrent = b.isCurrent !== false;
      if (aCurrent && !bCurrent) {
        return -1;
      }
      if (!aCurrent && bCurrent) {
        return 1;
      }
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [devices]);

  return (
    <Stack
direction="row"
spacing={2}
flexWrap="wrap"
alignItems="center"
    >
      {showClient && (
        <FormControl
size="small"
sx={{ minWidth: 180 }}
        >
          <InputLabel>{t('common:common.client', 'Cliente')}</InputLabel>
          <Select
            value={selectedClient?.id || ''}
            label={t('common:common.client', 'Cliente')}
            onChange={e => {
              const client = clients.find(c => c.id === e.target.value) || null;
              selectClient(client);
            }}
          >
            <MenuItem value=""><em>{t('common:common.allClients', 'Todos os clientes')}</em></MenuItem>
            {clients.map(c => (
              <MenuItem
key={c.id}
value={c.id}
              >{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {showStore && (
        <FormControl
size="small"
sx={{ minWidth: 180 }}
disabled={!selectedClient}
        >
          <InputLabel>{t('common:common.store', 'Loja')}</InputLabel>
          <Select
            value={selectedStore?.id || ''}
            label={t('common:common.store', 'Loja')}
            onChange={e => {
              const store = stores.find(s => s.id === e.target.value) || null;
              selectStore(store);
            }}
          >
            <MenuItem value=""><em>{t('common:common.allStores', 'Todas as lojas')}</em></MenuItem>
            {stores.map(s => (
              <MenuItem
key={s.id}
value={s.id}
              >{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {showDevice && (
        <FormControl
size="small"
sx={{ minWidth: 180 }}
disabled={!selectedStore}
        >
          <InputLabel>{t('common:common.device', 'Dispositivo')}</InputLabel>
          <Select
            value=""
            label={t('common:common.device', 'Dispositivo')}
displayEmpty
          >
            <MenuItem value=""><em>{t('common:common.allDevices', 'Todos os dispositivos')}</em></MenuItem>
            {sortedDevices.map((d, index) => {
              const showDivider = index > 0 && sortedDevices[index - 1].isCurrent !== false && d.isCurrent === false;
              return [
                showDivider && <Divider key={`divider-${d.id}`} sx={{ my: 0.5 }} />,
                <MenuItem
                  key={d.id}
                  value={d.id}
                >
                  {d.name}{d.isCurrent === false ? ` (${t('common:common.historical', 'Histórico')})` : ''}
                </MenuItem>
              ];
            })}
          </Select>
        </FormControl>
      )}

      {showDateRange && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <DatePicker
            label={t('common:from', 'Início')}
            value={startDate || null}
            onChange={onStartDateChange}
            renderInput={(params) => <TextField
{...params}
size="small"
sx={{ width: 150 }}
            />}
          />
          <DatePicker
            label={t('common:to', 'Fim')}
            value={endDate || null}
            onChange={onEndDateChange}
            renderInput={(params) => <TextField
{...params}
size="small"
sx={{ width: 150 }}
            />}
          />
        </Box>
      )}
    </Stack>
  );
};

GatewayContagemFilters.propTypes = {
  showDateRange: PropTypes.bool,
  showClient: PropTypes.bool,
  showStore: PropTypes.bool,
  showDevice: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onStartDateChange: PropTypes.func,
  onEndDateChange: PropTypes.func,
};

export default GatewayContagemFilters;
